import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { SUPPORTED_LANGS, LANG_LABELS, LANG_SHORT, setCookie } from '../i18n';

const LanguageSwitcher = ({ mobile = false }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const switchLanguage = (lang) => {
    const currentLang = i18n.language?.substring(0, 2) || 'en';
    const pathWithoutLang = location.pathname.replace(new RegExp(`^/${currentLang}`), '') || '/';
    i18n.changeLanguage(lang);
    setCookie('zelina-lang', lang);
    navigate(`/${lang}${pathWithoutLang === '/' ? '' : pathWithoutLang}${location.search}`);
    setOpen(false);
  };

  const currentLang = i18n.language?.substring(0, 2) || 'en';

  if (mobile) {
    return (
      <div className="pt-4 mt-4 border-t" style={{ borderColor: 'var(--border)' }} data-testid="mobile-lang-switcher">
        <p className="text-[10px] font-bold uppercase tracking-[0.15em] mb-3" style={{ color: 'var(--gold)' }}>
          {i18n.t('nav.language')}
        </p>
        <div className="flex flex-wrap gap-2">
          {SUPPORTED_LANGS.map((lang) => (
            <button
              key={lang}
              onClick={() => switchLanguage(lang)}
              className={`px-4 py-2 text-sm rounded-full transition-all duration-300 border ${
                currentLang === lang
                  ? 'border-[var(--gold)] text-[var(--gold)] font-semibold'
                  : 'border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--text-primary)] hover:text-[var(--text-primary)]'
              }`}
              data-testid={`lang-mobile-${lang}`}
            >
              {LANG_LABELS[lang]}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 h-10 px-2.5 rounded-full transition-all duration-300 hover:bg-[var(--gold-muted)]"
        data-testid="language-switcher"
        title={LANG_LABELS[currentLang]}
      >
        <Globe className="w-[16px] h-[16px]" style={{ color: 'var(--text-secondary)' }} strokeWidth={1.5} />
        <span className="text-xs font-bold tracking-wider" style={{ color: 'var(--text-secondary)' }}>
          {LANG_SHORT[currentLang] || 'EN'}
        </span>
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-3 w-48 rounded-xl overflow-hidden border"
          style={{
            background: 'var(--bg-elevated)',
            borderColor: 'var(--border)',
            boxShadow: '0 16px 48px rgba(0,0,0,0.4)',
            zIndex: 100,
          }}
          data-testid="language-dropdown"
        >
          <div className="px-4 py-2.5 border-b" style={{ borderColor: 'var(--border)' }}>
            <p className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: 'var(--gold)' }}>
              {i18n.t('nav.language')}
            </p>
          </div>
          {SUPPORTED_LANGS.map((lang) => (
            <button
              key={lang}
              onClick={() => switchLanguage(lang)}
              className={`w-full text-left px-4 py-3 text-sm transition-all duration-200 flex items-center justify-between ${
                currentLang === lang ? 'font-semibold' : 'hover:bg-[var(--gold-muted)]'
              }`}
              style={{ color: currentLang === lang ? 'var(--gold)' : 'var(--text-secondary)' }}
              data-testid={`lang-${lang}`}
            >
              <span className="flex items-center gap-3">
                <span className="text-[10px] font-bold tracking-wider w-5 opacity-50">{LANG_SHORT[lang]}</span>
                <span>{LANG_LABELS[lang]}</span>
              </span>
              {currentLang === lang && (
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--gold)' }} />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
